import { LIVELIKE_DATA_SOURCES } from '@libs/constants';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { extractSortedBadgeIds, getActiveBadge, getBadgeUrlsByActivity } from '../utils/BadgeUtils';
import { IActiveBadge } from '../types/iBadge';
import { extractQuests, getQuestsByActivity } from '../utils/QuestUtils';
import { IStore } from '../../../interfaces/StoreTypes';
import {
    fetchBackendBadges,
    fetchSDKAllBadges,
    fetchSDKEarnedBadges,
    fetchSDKEarnedBadgesByDate,
} from '../services/BadgeService';
import { fetchSDKRewardBalance } from '../services/BalanceService';
import { initializeLiveLike } from '../services/LiveLikeService';
import { fetchBackendQuests, fetchSDKAllQuests } from '../services/QuestService';
import { RewardCoin, toast } from '@intigral/shared';
import { get, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { fetchNotificationTransactions } from '../services/NotificationService';
import { __ } from '../../../translations';
import moment from 'moment';
import useSwitchProfileData from '../../../hooks/helper/useSwitchProfileData';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import { isGamificationEnabled } from '../utils/GamificationUtils';
import { isGuestUser } from '../../../config/guestMode';
import user from '../../../helpers/user';
import { GAMIFICATION_NOTIFICATIONS } from '@intigral/constant';
import { setCookie, getCookie } from '@intigral/utils';

const useGamification = () => {
    // Using React Query instead of Redux for configuration
    const { gamificationConfigs = {} } = useConfigurationSections();
    const gamificationConfig = isGamificationEnabled();

    const {
        liveLikeProfileId: profileId = null,
        liveLikeAccessToken: accessToken = null,
        profileGuid,
    } = useSelector((state: IStore) => state.profile.selectedProfile) ?? {};
    const {
        liveLikeClientId: clientId = null,
        rewardItemId = null,
        liveLikeTransactionsIntervalInHours = null,
        pointsRedeemThreshold = null,
    } = (gamificationConfigs as any) || {};
    // SDK State
    const [initializedLiveLike, setInitializedLiveLike] = useState(null);

    // Loading States
    const [badgeLoading, setBadgeLoading] = useState<boolean>(true);
    const [questsLoading, setQuestsLoading] = useState<boolean>(true);
    const [balanceLoading, setBalanceLoading] = useState<boolean>(true);

    // Badges State
    const [allBadges, setAllBadges] = useState(null);
    const [badgesSource, setBadgesSource] = useState(null);
    const [activeBadge, setActiveBadge] = useState<IActiveBadge | null>(null);

    // Quests State
    const [allQuests, setAllQuests] = useState(null);
    const [questsSource, setQuestsSource] = useState(null);

    const [balance, setBalance] = useState(0);

    const isLiveLikeProfile = Boolean(profileId);
    const { isProfileSwitching } = useSwitchProfileData(isLiveLikeProfile);

    const loadBadges = async () => {
        if (isGuestUser(user.getUserInfo())) {
            setAllBadges([]);
            setActiveBadge(null);
            setBadgeLoading(false);
            return;
        }
        try {
            setBadgeLoading(true);
            let source = badgesSource;
            let badgesData = [];
            let currentBadge = null;

            if (isLiveLikeProfile && badgesSource !== LIVELIKE_DATA_SOURCES.SDK && initializedLiveLike) {
                source = LIVELIKE_DATA_SOURCES.SDK;
                badgesData = await fetchSDKAllBadges(initializedLiveLike);
                const earnedBadges = await fetchSDKEarnedBadges(initializedLiveLike, profileId);
                const activeBadges = earnedBadges.length > 0 ? earnedBadges : badgesData;
                const activeIndex = earnedBadges.length > 0 ? null : 0;
                currentBadge = getActiveBadge(activeBadges, gamificationConfigs, activeIndex);
            } else if (!isLiveLikeProfile && badgesSource !== LIVELIKE_DATA_SOURCES.BACKEND) {
                source = LIVELIKE_DATA_SOURCES.BACKEND;
                badgesData = await fetchBackendBadges();
                currentBadge = getActiveBadge(badgesData, gamificationConfigs, 0);
            }

            setBadgesSource(source);
            setAllBadges(badgesData);
            setActiveBadge(currentBadge);
        } catch (error) {
            setBadgesSource(null);
            setAllBadges([]);
        } finally {
            setBadgeLoading(false);
        }
    };

    const loadQuests = async () => {
        if (isGuestUser(user.getUserInfo())) {
            setAllQuests([]);
            setQuestsLoading(false);
            return;
        }
        try {
            setQuestsLoading(true);
            let source = questsSource;
            let questsData = [];

            if (isLiveLikeProfile && questsSource !== LIVELIKE_DATA_SOURCES.SDK && initializedLiveLike) {
                questsData = await fetchSDKAllQuests(initializedLiveLike);
                source = LIVELIKE_DATA_SOURCES.SDK;
            } else if (!isLiveLikeProfile && questsSource !== LIVELIKE_DATA_SOURCES.BACKEND) {
                questsData = await fetchBackendQuests();
                source = LIVELIKE_DATA_SOURCES.BACKEND;
            }

            setQuestsSource(source);
            setAllQuests(questsData);
        } catch (error) {
            setQuestsSource(null);
            setAllQuests([]);
        } finally {
            setQuestsLoading(false);
        }
    };

    const getBalance = async () => {
        if (!initializedLiveLike || !rewardItemId) {
            setBalance(0);
            setBalanceLoading(false);
            return;
        }

        try {
            setBalanceLoading(true);
            const rewardBalance = (await fetchSDKRewardBalance(initializedLiveLike, [rewardItemId])) ?? [];
            const points = rewardBalance?.[0]?.reward_item_balance ?? 0;
            setBalance(points);
        } catch (error) {
            setBalance(0);
        } finally {
            setBalanceLoading(false);
        }
    };

    const displayNotifications = (notifications) => {
        // const storedNotifications = get(GAMIFICATION_NOTIFICATIONS?.STORED_NOTIFICARIONS) || [];
        const storedNotifications = getCookie(GAMIFICATION_NOTIFICATIONS?.STORED_NOTIFICARIONS) || [];
        const parsedStoredNotifications =
            typeof storedNotifications === 'string' ? JSON.parse(storedNotifications) : storedNotifications;

        const storedIds = new Set(parsedStoredNotifications.map((item) => item.id));
        const newNotifications = notifications.filter((item) => !storedIds.has(item.id));
        if (newNotifications.length) {
            newNotifications?.forEach((notification, index) => {
                setTimeout(() => {
                    if (notification?.source.includes('debit')) {
                        toast(
                            __('gamification_redeemed_notification')
                                .replace('{redeemedPoints}', Math.abs(notification?.reward_item_amount))
                                .replace('{totalPoints}', balance),
                            'default',
                            {
                                notDismissible: true,
                                icon: null,
                                shouldSlideDown: true,
                            }
                        );
                    } else {
                        if (balance >= pointsRedeemThreshold) {
                            toast(
                                __('gamification_earned_points_notification_title').replace(
                                    '{earnedPoints}',
                                    notification?.reward_item_amount
                                ),
                                'default',
                                {
                                    notDismissible: true,
                                    icon: <img src={__('gamificationPointsImage')} alt={'gamificationPointsImage'} />,
                                    shouldSlideDown: true,
                                }
                            );
                        } else {
                            toast(
                                __('gamification_earned_points_notification').replace(
                                    '{earnedPoints}',
                                    notification?.reward_item_amount
                                ),
                                'default',
                                {
                                    notDismissible: true,
                                    icon: <img src={__('gamificationPointsImage')} alt={'gamificationPointsImage'} />,
                                    shouldSlideDown: true,
                                }
                            );
                        }
                    }
                }, index * 3000);
            });
        }
        // after display the new notification to the localStorage also filter out the oldest notification before store to local sotrage
        const combinedNotification = [...parsedStoredNotifications, ...newNotifications];
        const targetTime = moment().subtract(liveLikeTransactionsIntervalInHours, 'hours').format('x');
        const filterOlderNotification = combinedNotification
            .filter((item) => targetTime < moment(item?.created_at).format('x'))
            .map((item) => ({ id: item.id, created_at: item.created_at }));
        // set(GAMIFICATION_NOTIFICATIONS?.STORED_NOTIFICARIONS, filterOlderNotification);
        setCookie(
            GAMIFICATION_NOTIFICATIONS?.STORED_NOTIFICARIONS,
            JSON.stringify(filterOlderNotification),
            1000,
            false,
            false
        );
    };

    const displayBadgesNotifications = (badge: IActiveBadge) => {
        if (!badge) return;
        // const storedBadges = get(GAMIFICATION_NOTIFICATIONS?.STORED_BADGES) || [];
        const storedBadges = getCookie(GAMIFICATION_NOTIFICATIONS?.STORED_BADGES) || [];
        const parsedStoredBadges = typeof storedBadges === 'string' ? JSON.parse(storedBadges) : storedBadges;
        const isBageExist = parsedStoredBadges.find((item) => item?.id === badge?.badge?.id);

        if (!isBageExist) {
            toast(__('gamification_earned_badge_notification').replace('{badgeName}', badge?.badge?.name), 'default', {
                notDismissible: true,
                icon: <img src={badge?.badge?.badge_icon_url} alt={badge?.badge?.name} />,
                shouldSlideDown: true,
            });
            const combinedBadges = [...parsedStoredBadges, badge.badge];
            // set(GAMIFICATION_NOTIFICATIONS?.STORED_BADGES, combinedBadges);
            setCookie(GAMIFICATION_NOTIFICATIONS?.STORED_BADGES, JSON.stringify(combinedBadges), 1000, false, false);
        }
    };

    const getNotifications = async () => {
        if (!gamificationConfig?.enable) return;
        try {
            const result = await fetchNotificationTransactions(
                initializedLiveLike,
                profileId,
                liveLikeTransactionsIntervalInHours
            );
            if (result?.length) {
                const filteredDublicateNotifications = result.filter(function (item) {
                    if (!this[item.invoked_action_id]) return (this[item.invoked_action_id] = true);
                }, Object.create(null));
                displayNotifications(filteredDublicateNotifications);
            }
        } catch (error) {
            setBadgesSource(null);
            setAllBadges([]);
        }
        try {
            const earnedBadges = await fetchSDKEarnedBadgesByDate(
                initializedLiveLike,
                profileId,
                liveLikeTransactionsIntervalInHours
            );
            const activeBadges = earnedBadges.length > 0 ? earnedBadges : [];
            const activeIndex = earnedBadges.length > 0 ? null : 0;
            const currentBadge = getActiveBadge(activeBadges, gamificationConfigs, activeIndex);
            if (currentBadge?.badge) {
                displayBadgesNotifications(currentBadge);
            }
        } catch (error) {
            setBadgesSource(null);
            setAllBadges([]);
        }
    };

    useEffect(() => {
        if (profileId && !initializedLiveLike && isProfileSwitching) {
            setBadgeLoading(true);
            setQuestsLoading(true);
            setBalanceLoading(true);
            return;
        }
        loadBadges();
        loadQuests();
        getBalance();
    }, [initializedLiveLike, profileId]);

    useEffect(() => {
        if (initializedLiveLike && balance && profileId && activeBadge) {
            getNotifications();
        }
    }, [initializedLiveLike, profileId, balance, activeBadge]);

    useEffect(() => {
        if (initializedLiveLike || !accessToken || !clientId) return;

        initializeLiveLike({ clientId, accessToken })?.then((client) => {
            setInitializedLiveLike(client);
        });
    }, [clientId, accessToken, profileId, initializedLiveLike]);

    const badges = useMemo(() => {
        if (activeBadge !== null) {
            const ids = extractSortedBadgeIds(allBadges);
            return getBadgeUrlsByActivity(ids, activeBadge, gamificationConfigs);
        } else {
            return [];
        }
    }, [allBadges, activeBadge, gamificationConfigs]);

    const { earnQuests, redeemQuests } = useMemo(() => {
        const getQuests = (type: string) => {
            const filteredQuests = extractQuests(allQuests, type);
            return getQuestsByActivity(filteredQuests, gamificationConfigs);
        };

        return {
            earnQuests: getQuests('Earn'),
            redeemQuests: getQuests('Redeem'),
        };
    }, [allQuests]);

    return {
        badges,
        activeBadge,
        earnQuests,
        redeemQuests,
        balance,
        isLiveLikeProfile,
        badgeLoading,
        questsLoading,
        balanceLoading,
        getNotifications,
    };
};

export default useGamification;
